import { ReactNode } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { LoaderOverlay } from 'core-components/library/loader'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type { IPartnerDocumentSection } from '../../../../lib/documentsAndLinks'
import { CockpitSectionHeader } from '../../../components/cockpitSectionHeader'
import type { TDocumentType } from '../../types'
import { CockpitDocumentLinks } from '../cockpitDocumentLinks/CockpitDocumentLinks'
import { EltCreateControls } from '../eltCreateControls/EltCreateControls'
import { QbrCreateControls } from '../qbrCreateControls/QbrCreateControls'

import styles from './CockpitDocumentsBrief.module.scss'

interface ICockpitDocumentsBriefProps {
  exportPlan?: ReactNode
  sections: IPartnerDocumentSection[]
  creatingDocuments?: Partial<Record<TDocumentType, boolean>>
  onCreatingChange?: (type: TDocumentType, isCreating: boolean) => void
  onView?: (type: TDocumentType) => void
  partnerId?: string
  isManagerCockpitMode?: boolean
}

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks

export const CockpitDocumentsBrief = ({
  sections,
  partnerId,
  isManagerCockpitMode = false,
  exportPlan,
  onView,
  creatingDocuments = {},
  onCreatingChange,
}: ICockpitDocumentsBriefProps) => {
  const t = useSafetyTranslation()
  const counts = sections.reduce(
    (totals, section) => ({
      elt: totals.elt + section.eltDocuments.length,
      qbr: totals.qbr + section.qbrDocuments.length,
    }),
    { elt: 0, qbr: 0 }
  )
  const singlePartner = sections.length === 1 ? sections[0] : undefined
  const canCreate =
    !!partnerId && singlePartner?.partnerId === partnerId.trim() && !isManagerCockpitMode

  return (
    <div className={styles.root}>
      <div className={styles.types}>
        {(['elt', 'qbr'] as const).map((type) => (
          <div key={type} className={styles.typeCard} aria-busy={!!creatingDocuments[type]}>
            <div className={styles.view}>
              <button
                type="button"
                className={styles.label}
                aria-label={t(
                  type === 'elt' ? KEYS.quickActions.viewElt : KEYS.quickActions.viewQbr
                )}
                disabled={!onView || creatingDocuments[type]}
                onClick={() => onView?.(type)}
              >
                <Translation
                  span
                  text={type === 'elt' ? KEYS.createRow.typeElt : KEYS.createRow.typeQbr}
                />
              </button>
            </div>
            <span className={styles.count}>{counts[type]}</span>
            <div className={styles.actions}>
              {canCreate &&
                singlePartner &&
                (type === 'elt' ? (
                  <EltCreateControls
                    iconOnly
                    onCreatingChange={(isCreating) => onCreatingChange?.(type, isCreating)}
                    partnerId={singlePartner.partnerId}
                    documents={singlePartner.eltDocuments}
                    isCreating={creatingDocuments[type]}
                  />
                ) : (
                  <QbrCreateControls
                    iconOnly
                    onCreatingChange={(isCreating) => onCreatingChange?.(type, isCreating)}
                    partnerId={singlePartner.partnerId}
                    rtm={singlePartner.rtm}
                  />
                ))}
            </div>
            <LoaderOverlay
              show={!!creatingDocuments[type]}
              dimension={{ width: 24, height: 24 }}
              className={styles.loader}
            />
          </div>
        ))}
      </div>
      {exportPlan}
      <div>
        <CockpitSectionHeader>
          <Translation span text={KEYS.sectionLinks} />
        </CockpitSectionHeader>
        <CockpitDocumentLinks
          partnerId={singlePartner?.partnerId}
          partnerRtm={isManagerCockpitMode ? undefined : singlePartner?.rtm}
        />
      </div>
    </div>
  )
}

CockpitDocumentsBrief.displayName = 'CockpitDocumentsBrief'
